// packages/web/src/components/marketplace/BuyBoxOptionPerks.tsx
// Minimal details panel for included service / legacy free service buy-box perks.

import { Inventory2Outlined as BoxIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React from 'react';

interface BuyBoxOptionPerksProps {
  option: any;
  isBaseService: boolean;
}

function getSavingsCents(option: any): number | null {
  const candidates = [
    option?.savings_display_cents,
    option?.savings_cents,
    option?.metadata?.savings_display_cents,
    option?.metadata?.savings_cents,
    option?.rebate_amount,
  ];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }

  return null;
}

function getTermMonths(option: any): number | null {
  const candidates = [
    option?.included_service_term_months,
    option?.metadata?.included_service_term_months,
    option?.duration_months,
  ];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }

  return null;
}

function getFulfillmentText(option: any): string {
  if (
    typeof option?.fulfillment_requirements === 'string' &&
    option.fulfillment_requirements.trim()
  ) {
    return option.fulfillment_requirements;
  }

  return 'Your advisor will coordinate the included service after your primary service activation is confirmed.';
}

export const BuyBoxOptionPerks: React.FC<BuyBoxOptionPerksProps> = ({ option, isBaseService }) => {
  const isIncludedService =
    option?.perk_type === 'included_service' || option?.perk_type === 'free_service';
  if (isBaseService || !isIncludedService) return null;

  const savingsCents = getSavingsCents(option);
  const termMonths = getTermMonths(option);
  const setupIsIncluded = option?.setup_fee_free !== false;
  const fullTermFree = option?.full_term_free !== false;
  const fulfillmentText = getFulfillmentText(option);

  return (
    <Box
      sx={{
        border: '1px solid #bfdbfe',
        borderRadius: 2,
        mt: 1,
        bgcolor: '#eff6ff',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        {savingsCents ? (
          <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 700, mb: 0.75 }}>
            Free included service · saves ${(savingsCents / 100).toLocaleString()}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 700, mb: 0.75 }}>
            Free included service
          </Typography>
        )}

        {termMonths ? (
          <Typography variant="body2" sx={{ color: '#424242', mb: 0.4 }}>
            • Contract term covered: {termMonths} month{termMonths === 1 ? '' : 's'}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ color: '#424242', mb: 0.4 }}>
          • Full term free: {fullTermFree ? 'Yes' : 'Varies'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#424242', mb: 0.4 }}>
          • Setup included: {setupIsIncluded ? 'Yes' : 'No'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1, opacity: 0.88 }}>
          <BoxIcon sx={{ fontSize: '0.9rem', color: '#757575', mt: 0.1, flexShrink: 0 }} />
          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: '#616161', display: 'block', fontSize: '0.72rem' }}
            >
              Fulfillment note
            </Typography>
            <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.72rem' }}>
              {fulfillmentText}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
